// ==============================================
// FACEBOOK CONVERSION API SERVICE
// For: The Peters Agriculture - Super Gro Fertilizer
// ==============================================

// Configuration - UPDATE THESE WITH YOUR VALUES
const CONFIG = {
  PIXEL_ID: process.env.REACT_APP_FACEBOOK_PIXEL_ID || '614812224588390',
  BACKEND_URL: process.env.REACT_APP_BACKEND_URL || 'https://supergro-backend.onrender.com',
};

// Product Information
const PRODUCT = {
  id: 'supergro-5l',
  name: 'Super Gro Organic Fertilizer',
  price: 65000,
  currency: 'NGN',
  category: 'Agriculture',
};

// ==============================================
// CORE EVENT SENDING FUNCTION
// ==============================================
export const sendFacebookEvent = async (
  eventName: string,
  customData?: any,
  formData?: any
): Promise<boolean> => {
  try {
    // Don't send in development if not configured
    if (process.env.NODE_ENV === 'development' && !CONFIG.PIXEL_ID) {
      console.log(`[DEV] Facebook Event: ${eventName}`, customData);
      return true;
    }

    // Prepare user data
    const userData: any = {
      client_user_agent: navigator.userAgent,
    };

    // Add form data if available
    if (formData) {
      if (formData.email) {
        userData.em = formData.email.toLowerCase().trim();
      }
      if (formData.phone) {
        userData.ph = formData.phone.replace(/\D/g, '');
      }
      if (formData.firstName) {
        userData.fn = formData.firstName.toLowerCase().trim();
      }
      if (formData.lastName) {
        userData.ln = formData.lastName.toLowerCase().trim();
      }
    }

    // Create Facebook event
    const event = {
      event_name: eventName,
      event_time: Math.floor(Date.now() / 1000),
      action_source: 'website',
      user_data: userData,
      custom_data: {
        currency: PRODUCT.currency,
        value: PRODUCT.price,
        content_ids: [PRODUCT.id],
        content_type: 'product',
        content_name: PRODUCT.name,
        content_category: PRODUCT.category,
        ...customData,
      },
      event_source_url: window.location.href,
      event_id: `event_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    };

    console.log(`📊 Sending Facebook Event: ${eventName}`);

    // Send to your backend
    const response = await fetch(`${CONFIG.BACKEND_URL}/api/facebook-event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        pixelId: CONFIG.PIXEL_ID,
        eventData: {
          data: [event],
          test_event_code: process.env.REACT_APP_FACEBOOK_TEST_EVENT_CODE || '',
        },
      }),
    });

    const result = await response.json();
    
    if (result.success) {
      console.log(`✅ Facebook Event "${eventName}" sent successfully`);
    } else {
      console.warn(`⚠️ Facebook Event "${eventName}" may not have been tracked`);
    }

    return true;

  } catch (error) {
    console.error(`❌ Error sending Facebook event "${eventName}":`, error);
    return false;
  }
};

// ==============================================
// SPECIFIC EVENT FUNCTIONS FOR YOUR SALES PAGE
// ==============================================

// 1. TRACK PAGE VIEW - When someone visits your page
export const trackPageView = () => {
  return sendFacebookEvent('PageView', {
    content_name: 'Super Gro Homepage',
    value: 0,
  });
};

// 2. TRACK PRODUCT VIEW - When someone views the product
export const trackViewContent = () => {
  return sendFacebookEvent('ViewContent', {
    content_name: PRODUCT.name,
    value: PRODUCT.price,
  });
};

// 3. TRACK LEAD - When someone shows interest
export const trackLead = (buttonName?: string) => {
  return sendFacebookEvent('Lead', {
    button_name: buttonName || 'order_button',
    value: 0,
  });
};

// 4. TRACK INITIATE CHECKOUT - When someone starts checkout
export const trackInitiateCheckout = () => {
  return sendFacebookEvent('InitiateCheckout', {
    num_items: 1,
    value: PRODUCT.price,
  });
};

// 5. TRACK PURCHASE - When someone completes purchase
export const trackPurchase = (transactionId: string, formData?: any) => {
  return sendFacebookEvent('Purchase', {
    transaction_id: transactionId,
    num_items: 1,
    value: PRODUCT.price,
  }, formData);
};

// 6. TRACK BUTTON CLICK - Track specific button clicks
export const trackButtonClick = (buttonName: string, pageSection?: string) => {
  return sendFacebookEvent('CustomizeProduct', {
    custom_event_type: 'button_click',
    button_name: buttonName,
    page_section: pageSection,
    value: 0,
  });
};

// 7. INITIALIZE TRACKING - Call this when app starts
export const initFacebookTracking = () => {
  trackPageView();
  trackViewContent();
  console.log('🎯 Facebook Conversion API Initialized');
};

// Export configuration
export const facebookConfig = CONFIG;
